import React, { useState, useRef, useEffect } from 'react'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faRightFromBracket } from '@fortawesome/free-solid-svg-icons'
import useAuth from '../../hooks/useAuth'
import useAxiosPrivate from '../../hooks/useAxiosPrivate'
import useLogout from '../../hooks/useLogout'
import PersonalDetailsSection from '../../components/ProfileForm/PersonalDetailsSection'
import ContactInformationSection from '../../components/ProfileForm/ContactInformationSection'
import AddressSection from '../../components/ProfileForm/AddressSection'
import GPDetailsSection from '../../components/ProfileForm/GPDetailsSection'
import MedicalHistorySection from '../../components/ProfileForm/MedicalHistorySection'
import { toast } from 'sonner'
import EmergencyContactInfo from './Partials/EmergencyContactInfo'

const Profile = () => {
  const { auth, setAuth, setShowLoader } = useAuth()
  const axios = useAxiosPrivate()
  const [profileData, setProfileData] = useState(null)
  const [userInfo, setUserInfo] = useState(null)
  const [isNotRegisteredWithGP, setIsNotRegisteredWithGP] = useState(false)
  const [showGpMap, setShowGpMap] = useState(false)
  const [enterDetailsManually, setEnterDetailsManually] = useState(false)
  const [manualDetails, setManualDetails] = useState({
    name: '',
    addressLine1: '',
    addressLine2: '',
    city: '',
    postcode: '',
    country: 'United Kingdom',
    phone: '',
    email: '',
  })

  const [emergencyContact, setEmergencyContact] = useState({
    emergencyName: "",
    emergencyPhone: ""
  })

  const [selectedGP, setSelectedGP] = useState(null)
  

  const logout = useLogout()
  useEffect(() => { }, [selectedGP])
  useEffect(() => {
    if (!auth?.user) return
    
    const [year, month, day] = auth?.user?.date_of_birth?.split('-') || []
    setProfileData({
      first_name: auth.user.name?.split(' ')[0] || '',
      last_name: auth.user.name?.split(' ')[1] || '',
      email: auth.user.email || '',
      phone_number: auth.user.phone_number || '',
      date_of_birth: { day, month, year },
      profile_pic: auth.user.profile_pic || '',
      profile_colour: auth.user.profile_colour || '',
      background_pic: auth.user.background_pic || '',
    })
    setUserInfo({
      gender: auth.user.user_info?.gender || 'Male',
      country: auth.user.user_info?.country || 'united kingdom',
      language: auth.user.user_info?.language || 'English',
      address: auth.user.user_info?.address || {
        addressLine1: '',
        addressLine2: '',
        city: '',
        postcode: '',
        country: 'United Kingdom',
        countryCode: 'GB',
      },
      gp: auth.user.user_info?.gp ||
        auth.user.user_info?.gp || {
        name: '',
        phone: '',
        email: '',
        address: {
          addressLine1: '',
          addressLine2: '',
          city: '',
          postcode: '',
          country: 'United Kingdom',
          countryCode: 'GB',
        },
      },
      emergencyContact: {
        name: auth.user.user_info?.emergencyContact?.name || '',
        phone: auth.user.user_info?.emergencyContact?.phone || '',
      },
      medicalHistory: auth.user.user_info?.medicalHistory || {
        allergies: {
          preferNotToSay: false,
          noKnownAllergies: false,
          value: '',
        },
        medications: { takingNone: false, preferNotToSay: false, value: '' },
        conditions: {
          noKnownConditions: false,
          preferNotToSay: false,
          value: '',
        },
      },
    })
    
    setEmergencyContact({
      emergencyName: auth.user.user_info?.emergencyContact?.name || '',
      emergencyPhone: auth.user.user_info?.emergencyContact?.phone || '',
    })

    // Initialize selectedGP if GP data exists
    // Check both possible locations for GP data
    const gpData = selectedGP || auth.user.user_info?.gp

    if (
      gpData &&
      Object.keys(gpData).length > 0 &&
      (gpData.name || gpData.surgeryName)
    ) {
      // Format the GP data for display in the component
      const formattedGP = {
        ...gpData,
        // If address is an object, convert it to a string for display
        address:
          gpData.address && typeof gpData.address === 'object'
            ? [
              gpData.address.addressLine1,
              gpData.address.addressLine2,
              gpData.address.city,
            ]
              .filter(Boolean)
              .join(', ')
            : gpData.address,
        // Ensure we have the postcode from the address object if needed
        postcode: gpData.postcode || gpData.address?.postcode,
      }
      setSelectedGP(formattedGP)
      // Don't show the map if GP is already selected
      setShowGpMap(false)
      // Also make sure we're not in "not registered" mode
      setIsNotRegisteredWithGP(false)
    } else {
      // Clear selectedGP if no GP data exists
      setSelectedGP(null)
      // Only show map if user hasn't indicated they're not registered
      if (!auth.user.user_info?.isNotRegisteredWithGP) {
        setShowGpMap(false) // Start with map hidden, user can choose to show it
      }
    }
  }, [auth?.user])

  

  const handleSubmit = async (e) => {
    e.preventDefault()

    setShowLoader(true)

    try {
      const formData = new FormData()

      if (!userInfo.gp && !selectedGP) {
        userInfo.gp = manualDetails
      } else {
        userInfo.gp = selectedGP || userInfo.gp
      }

      userInfo.emergencyContact = {
        name: emergencyContact.emergencyName,
        phone: emergencyContact.emergencyPhone
      }

      const data = {
        data: profileData,
        info: userInfo,
        user_id: auth?.user?.id,
      }
      const response = await axios.post('/profile/update', data, {})

      if (!response.data.error) {
        toast.success('Profile updated successfully!')
        window.location.reload()
      }
    } catch (error) {
      toast.error('Failed to update profile. Please try again.')
    } finally {
      setShowLoader(false)
    }
  }

  

  const handleClearSelectedGP = async () => {
    setSelectedGP(null)
    setEnterDetailsManually(false)
    setManualDetails({
      name: '',
      addressLine1: '',
      addressLine2: '',
      city: '',
      postcode: '',
      country: '',
      phone: '',
      email: '',
    })
    setIsNotRegisteredWithGP(false)
    setShowGpMap(false)

    // Update profile immediately to clear GP data
    try {
      const updatedUserInfo = {
        ...userInfo,
        gp: null,
      }
      setUserInfo(updatedUserInfo)

      const data = {
        data: profileData,
        info: updatedUserInfo,
        user_id: auth?.user?.id,
      }

      const response = await axios.post('/profile/update', data)

      if (!response.data.error) {
        // Update auth context
        setAuth((prev) => ({
          ...prev,
          user: {
            ...prev.user,
            user_info: {
              ...prev.user.user_info,
              gp: null,
            },
          },
        }))

        toast.success('GP details cleared successfully!')
      }
    } catch (error) {
      console.error('Error clearing GP details:', error)
      toast.error('Failed to clear GP details. Please try again.')
    }
  }

  const handleEditSelectedGP = () => {
    if (!selectedGP) return
    setEnterDetailsManually(true)
    const parts = selectedGP.address.split(',').map((part) => part.trim())
    const line1 = parts[0] || ''
    const line2 = parts[1] || ''
    const town = parts[2] || ''

    setManualDetails({
      name: selectedGP.name || selectedGP.surgeryName || '',
      addressLine1: line1 || '',
      addressLine2: line2 || '',
      city: town || '',
      postcode: selectedGP.postcode || '',
      country: selectedGP.country || '',
      phone: selectedGP.phone || '',
      email: selectedGP.email || '',
    })
  }

  return (
    <section className="pb-16">
      {/* <MainTitle title="Account" /> */}
      <div className="flex-1 xl:overflow-y-auto pb-10 max-w-4xl mx-auto">
        <form className="divide-y divide-slate-200" onSubmit={handleSubmit}>
          <div className="grid gap-y-6 pb-8 grid-cols-6 gap-x-6">
            <PersonalDetailsSection
              profileData={profileData}
              setProfileData={setProfileData}
              userInfo={userInfo}
              setUserInfo={setUserInfo}
              showTitle={true}
            />
          </div>

          <div className="grid grid-cols-1 gap-y-6 py-8 sm:grid-cols-6 sm:gap-x-6">
            <ContactInformationSection
              profileData={profileData}
              setProfileData={setProfileData}
              userInfo={userInfo}
              setUserInfo={setUserInfo}
            />
            <AddressSection
              userInfo={userInfo}
              setUserInfo={setUserInfo}
              profileData={profileData}
              setProfileData={setProfileData}
              showTitle={false}
            />
          </div>

          {/* Emergency Contact */}
          <div className="grid grid-cols-1 gap-y-6 py-8 sm:gap-x-6">
            <EmergencyContactInfo
              emergencyContact={emergencyContact}
              setEmergencyContact={setEmergencyContact}
            />
          </div>

          <div className="grid grid-cols-1 gap-y-6 py-8 sm:grid-cols-6 sm:gap-x-6">
            <GPDetailsSection
              selectedGP={selectedGP}
              setSelectedGP={setSelectedGP}
              showGpMap={showGpMap}
              setShowGpMap={setShowGpMap}
              enterDetailsManually={enterDetailsManually}
              setEnterDetailsManually={setEnterDetailsManually}
              manualDetails={manualDetails}
              setManualDetails={setManualDetails}
              isNotRegisteredWithGP={isNotRegisteredWithGP}
              setIsNotRegisteredWithGP={setIsNotRegisteredWithGP}
              handleClearSelectedGP={handleClearSelectedGP}
              handleEditSelectedGP={handleEditSelectedGP}
              profileData={profileData}
              userInfo={userInfo}
              setUserInfo={setUserInfo}
            />
          </div>

          {/* Medical History */}
          <div className="grid grid-cols-1 gap-y-6 py-8 sm:grid-cols-6 sm:gap-x-6">
            <MedicalHistorySection
              userInfo={userInfo}
              setUserInfo={setUserInfo}
            />
          </div>



          <div className="flex justify-between items-center lg:justify-end gap-x-3 fixed bottom-[75px] lg:bottom-0 left-0 lg:left-44 right-0 bg-white border-t border-gray-200 px-4 py-6 sm:px-6 w-full lg:w-[calc(100%-176px)]">
            <div className="text-portal-dark-purple lg:hidden">
              <button
                onClick={logout}
                className="inline-flex justify-center rounded-md text-rose-500 hover:text-rose-600 text-sm font-semibold "
              >
                <FontAwesomeIcon
                  icon={faRightFromBracket}
                  className="text-xl"
                />
                <span className="pl-2">Logout</span>
              </button>
            </div>
            <div className="flex justify-end">
              <button
                type="submit"
                className="inline-flex justify-center rounded-md bg-hub-primary hover:bg-hub-primary-hover px-8 py-4 text-sm font-semibold text-white "
              >
                Update Profile
              </button>
            </div>
          </div>
        </form>
      </div>
    </section>
  )
}

export default Profile
